'use client'

import { useTranslations } from 'next-intl'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useUser } from '@auth0/nextjs-auth0'

import VesselDetailHeader from './VesselDetailHeader'
import VesselDetailStatus from './VesselDetailStatus'
import VesselCommunityUpdatesSection from './VesselCommunityUpdatesSection'
import CommunityUpdateEventItemPublicClient from './CommunityUpdateEventItemPublicClient'
import MapWithEffectiveLocalize from '../Map/MapWithEffectiveLocalize'
import NotificationsPublicClient from '../Notifications/NotificationsPublic/NotificationsPublicClient'
import NotificationsPrivate from '../Notifications/NotificationsPrivate/NotificationsPrivate'
import StatementOfFacts from './StatementOfFacts/StatementOfFacts'
import PortArrivalKPI from './PortArrivalKPI/PortArrivalKPI'

import { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { useActiveVessel } from '@/app/context/ActiveVesselContext'

import './VesselDetails.scss'

type TProps = {
  vessel?: TVesselJourneyResponse
}

export default function VesselDetailsClient({ vessel: vesselProp }: TProps) {
  const { activeVessel } = useActiveVessel()
  const vessel = vesselProp ?? activeVessel
  const t = useTranslations()
  const { user } = useUser()

  if (!vessel) {
    return <div className="no-vessel" />
  }

  return (
    <div className="vessel-details-parent">
      <section className="single-large vessel-map">
        <MapWithEffectiveLocalize vessel={vessel} />
      </section>
      <div className="vessel-details-container">
        {/* Vessel Header - always first */}
        <section className="vessel-header">
          {/* Hidden checkbox for CSS-only toggle */}
          <input
            type="checkbox"
            id="vessel-details-toggle"
            className="vessel--extra-details-checkbox"
            aria-label={t('VesselDetails.showMore')}
          />

          <VesselDetailHeader vessel={vessel} />
          {/* Expandable Details - shown under vessel info when checkbox is checked */}
          <div className="vessel--extra-details-container">
            <div className="vessel--extra-details-body">
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.flagstate')}</div>
                <div className="value">{vessel.ship?.country?.name}</div>
              </div>
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.draught')}</div>
                <div className="value">
                  {vessel.ship.draught != null
                    ? `${vessel.ship.draught.toFixed(2)} ${t('VesselDetails.units.meters')}`
                    : '-'}
                </div>
              </div>
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.grossTonnage')}</div>
                <div className="value">
                  {vessel.ship.grossTonnage} {t('VesselDetails.units.metricTons')}
                </div>
              </div>
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.dwt')}</div>
                <div className="value">
                  {vessel.ship.deadWeightTonnage} {t('VesselDetails.units.metricTons')}
                </div>
              </div>
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.loa')}</div>
                <div className="value">
                  {vessel.ship.lengthOverall}
                  {t('VesselDetails.units.meters')}
                </div>
              </div>
              <div className="vessel--extra-details-body-item">
                <div className="label">{t('VesselDetails.beamWidth')}</div>
                <div className="value">
                  {vessel.ship.width}
                  {t('VesselDetails.units.meters')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map - positioned differently on mobile vs desktop */}
        <section className="vessel-map">
          <MapWithEffectiveLocalize vessel={vessel} />
        </section>

        <section className="vessel-status">
          <VesselDetailStatus vessel={vessel} t={t} />
        </section>

        {/* Notifications */}
        <section className="vessel-notifications">
          <NotificationsPublicClient vessel={vessel} />
        </section>

        {/* SOF */}
        <StatementOfFacts vessel={vessel} />

        {/* Port of Arrival KPI */}
        <PortArrivalKPI vessel={vessel} />
      </div>
    </div>
  )
}
